import React, { useId, useState } from 'react';
import { Button } from '@/components/common';
import authAPI from '@/services/auth-api';
import { FaArrowLeft, FaEnvelope, FaExclamationTriangle, FaPaperPlane } from 'react-icons/fa';

interface ForgotPasswordFormProps {
  /** Back to the sign-in form. */
  onBack: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Forgot password?" The server answers the same way whether or not the
 * address has an account, so the confirmation is worded the same way too: it
 * never says an email was sent, only that one was sent if there is an account.
 */
export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const emailId = useId();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setFieldError('Enter a valid email address');
      return;
    }

    setFieldError(null);
    setError(null);
    setIsLoading(true);
    try {
      await authAPI.requestPasswordReset(trimmed);
      setSentTo(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div className="space-y-6">
        <div role="status" className="p-4 bg-game-medium/20 border border-game-medium/50 rounded-lg text-sm text-gray-200 space-y-2">
          <p>
            If an account exists for <span className="font-semibold text-white">{sentTo}</span>, we have sent
            it a link to reset the password.
          </p>
          <p>The link works for one hour. If nothing arrives in a few minutes, check your spam folder.</p>
        </div>
        <Button type="button" variant="secondary" onClick={onBack} className="w-full px-6 py-3">
          <FaArrowLeft />
          <span>Back to sign in</span>
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <p className="text-sm text-gray-200">
        Enter the email address of your account and we will send you a link to choose a new password.
      </p>

      {error && (
        <div className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
          <div className="flex items-center gap-3">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      <div>
        <label htmlFor={emailId} className="block text-sm font-semibold text-white mb-2">
          <div className="flex items-center gap-2">
            <FaEnvelope className="text-game-golden" />
            Email
          </div>
        </label>
        <input
          type="email"
          id={emailId}
          name="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (fieldError) setFieldError(null);
          }}
          required
          autoComplete="email"
          maxLength={255}
          className={`w-full px-4 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 ${
            fieldError ? 'border-game-orange bg-game-orange/20' : ''
          }`}
          placeholder="you@example.com"
        />
        {fieldError && (
          <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
            {fieldError}
          </div>
        )}
      </div>

      <Button type="submit" variant="primary" isLoading={isLoading} className="w-full px-6 py-3">
        <FaPaperPlane />
        <span>Send reset link</span>
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="w-full text-sm text-game-golden hover:text-game-accent underline-offset-2 hover:underline"
      >
        Back to sign in
      </button>
    </form>
  );
};
